import type { ProductMediaDetail, ProductMediaKind, ProductMediaSummaryItem } from "@school-sales-ai/shared";
import type { AgentType, BillingOverviewResponse, BillingProductOption, CatalogFormContext, ClientBillingDetailResponse, ClientTypeValue, DocumentEntry, Lead, LeadLabel, LeadLabelWithUsage, LlmUsageSummaryResponse, MyBillingResponse, DeploymentEngine, ModelEffort, PipelineStage, SelectableModelId, WhatsappTransport } from "@school-sales-ai/shared";
import type { CreateLeadCandidateInput, CreateLeadInput, CreateLeadLabelInput, CreatePipelineStageInput, UpdateDocumentInput, UpdateLeadCandidateInput, UpdateLeadInput, UpdateLeadLabelInput, UpdatePipelineStageInput } from "@school-sales-ai/shared";
import { DEFAULT_BRAND, type BrandKey } from "@school-sales-ai/shared";
import { supabase } from "./supabase";
import { useAuthStore } from "../stores/auth";
import { useClientSwitcherStore } from "../stores/client-switcher.store";

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error("Missing VITE_API_BASE_URL environment variable");
}

/**
 * A failed request, carrying enough to react to WHY it failed.
 *
 * Extends Error, so every existing `catch (err) { err.message }` keeps working; callers
 * that need to map a status onto a form field (a 409 slug conflict, say) can narrow to
 * this instead of matching on message text.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers: Record<string, string> = {};
  // FormData bodies set their own multipart Content-Type (with boundary) — never override it.
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  const activeClientId = useClientSwitcherStore.getState().activeClientId;
  if (activeClientId) {
    headers["X-Client-Id"] = activeClientId;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
  });

  if (response.status === 401) {
    await supabase.auth.signOut();
    useAuthStore.getState().setSession(null);
    window.location.href = "/login";
    throw new Error("Session expired");
  }

  if (!response.ok) {
    // The global error handler responds flat: { error: "CODE", message: "…" }.
    // Older callers may still produce { error: { message } } — read both.
    const body = await response.json().catch(() => ({})) as {
      error?: string | { message?: string };
      message?: string;
    };
    const nested = typeof body.error === "object" ? body.error?.message : undefined;
    throw new ApiError(
      body.message ?? nested ?? `Request failed: ${response.status}`,
      response.status,
      typeof body.error === "string" ? body.error : undefined,
    );
  }

  if (response.status === 204) return null as unknown as T;

  return response.json() as Promise<T>;
}

/**
 * Per-request options the callers may add. `keepalive` lets a request outlive
 * the page — the catalog editor flushes its pending edits with it when the tab
 * is hidden, since a closing tab would otherwise cancel the save mid-flight.
 */
type RequestOptions = Pick<RequestInit, "keepalive">;

export const api = {
  get: <T>(path: string): Promise<T> => apiFetch<T>(path),
  post: <T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> =>
    apiFetch<T>(path, { ...options, method: "POST", body: JSON.stringify(body) }),
  postForm: <T>(path: string, form: FormData): Promise<T> =>
    apiFetch<T>(path, { method: "POST", body: form }),
  patch: <T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> =>
    apiFetch<T>(path, { ...options, method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown): Promise<T> =>
    apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  putForm: <T>(path: string, form: FormData): Promise<T> =>
    apiFetch<T>(path, { method: "PUT", body: form }),
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    apiFetch<T>(
      path,
      body === undefined ? { method: "DELETE" } : { method: "DELETE", body: JSON.stringify(body) },
    ),
};

export async function createLeadApi(data: CreateLeadInput): Promise<Lead> {
  return api.post<Lead>("/crm/leads", data);
}

export async function updateLeadApi(
  leadId: string,
  data: UpdateLeadInput,
): Promise<Lead> {
  return api.patch<Lead>(`/crm/leads/${leadId}`, data);
}

export async function getCatalogContextApi(): Promise<CatalogFormContext> {
  return api.get<CatalogFormContext>("/crm/catalog/form-fields");
}

export async function deleteLeadApi(leadId: string): Promise<void> {
  return api.delete<void>(`/crm/leads/${leadId}`);
}

export async function restoreLeadApi(leadId: string): Promise<Lead> {
  return api.patch<Lead>(`/crm/leads/${leadId}/restore`, {});
}

export async function getDeletedLeadsApi(): Promise<Lead[]> {
  return api
    .get<{ items: Lead[] }>("/crm/leads/deleted")
    .then((r) => r.items);
}

// ── Documents API ──────────────────────────────────────────────────────────────

export async function listDocumentsApi(): Promise<DocumentEntry[]> {
  return api.get<DocumentEntry[]>("/crm/documents");
}

export async function uploadDocumentApi(data: {
  file: File;
  title: string;
  description: string;
}): Promise<DocumentEntry> {
  const form = new FormData();
  form.append("title", data.title);
  form.append("description", data.description);
  // File goes last so the text fields are already parsed when the backend reads the stream.
  form.append("file", data.file);
  return api.postForm<DocumentEntry>("/crm/documents", form);
}

export async function updateDocumentApi(
  id: string,
  data: UpdateDocumentInput,
): Promise<DocumentEntry> {
  return api.patch<DocumentEntry>(`/crm/documents/${id}`, data);
}

/** Swap the file behind a material; title, description and product are kept. */
export async function replaceDocumentFileApi(id: string, file: File): Promise<DocumentEntry> {
  const form = new FormData();
  form.append("file", file);
  return api.putForm<DocumentEntry>(`/crm/documents/${id}/file`, form);
}

export async function deactivateDocumentApi(id: string): Promise<DocumentEntry> {
  return api.patch<DocumentEntry>(`/crm/documents/${id}`, { isActive: false });
}

/**
 * Deletes for good. `/permanent`, not a bare DELETE: that route still only
 * deactivates, for CRM tabs opened before deleting existed.
 */
export async function deleteDocumentApi(id: string): Promise<void> {
  return api.delete<void>(`/crm/documents/${id}/permanent`);
}

// ── Product media API (docs/plans/product-media.md) ─────────────────────────────

export async function getProductMediaSummaryApi(): Promise<ProductMediaSummaryItem[]> {
  return api.get<ProductMediaSummaryItem[]>("/crm/product-media/summary");
}

export async function getProductMediaApi(productId: string): Promise<ProductMediaDetail> {
  return api.get<ProductMediaDetail>(`/crm/products/${productId}/media`);
}

/** Gallery: appends a photo. Video / pdf: sets or replaces the one file. */
export async function addProductMediaFileApi(
  productId: string,
  kind: ProductMediaKind,
  file: File,
): Promise<ProductMediaDetail> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm<ProductMediaDetail>(`/crm/products/${productId}/media/${kind}/files`, form);
}

export async function removeProductMediaFileApi(fileId: string): Promise<ProductMediaDetail> {
  return api.delete<ProductMediaDetail>(`/crm/product-media/files/${fileId}`);
}

export async function setProductMediaDescriptionApi(
  productId: string,
  kind: ProductMediaKind,
  description: string,
): Promise<ProductMediaDetail> {
  return api.patch<ProductMediaDetail>(`/crm/products/${productId}/media/${kind}`, {
    description,
  });
}

export async function reorderProductGalleryApi(
  productId: string,
  fileIds: string[],
): Promise<ProductMediaDetail> {
  return api.put<ProductMediaDetail>(`/crm/products/${productId}/media/gallery/order`, {
    fileIds,
  });
}

export async function getDocumentDownloadUrlApi(id: string): Promise<string> {
  return api.get<{ url: string }>(`/crm/documents/${id}/download`).then((r) => r.url);
}

// ── Pipeline stages (configurable Kanban columns) ────────────────────────────

export async function listPipelineStagesApi(): Promise<PipelineStage[]> {
  return api.get<PipelineStage[]>("/crm/pipeline-stages");
}

export async function createPipelineStageApi(
  data: CreatePipelineStageInput,
): Promise<PipelineStage> {
  return api.post<PipelineStage>("/crm/pipeline-stages", data);
}

export async function updatePipelineStageApi(
  id: string,
  data: UpdatePipelineStageInput,
): Promise<PipelineStage> {
  return api.patch<PipelineStage>(`/crm/pipeline-stages/${id}`, data);
}

export async function deletePipelineStageApi(id: string): Promise<void> {
  return api.delete<void>(`/crm/pipeline-stages/${id}`);
}

export async function reorderPipelineStagesApi(
  orderedIds: string[],
): Promise<PipelineStage[]> {
  return api.patch<PipelineStage[]>("/crm/pipeline-stages/reorder", { orderedIds });
}

// ── Lead labels (colored tags) ───────────────────────────────────────────────

export async function listLabelsApi(): Promise<LeadLabelWithUsage[]> {
  return api.get<LeadLabelWithUsage[]>("/crm/labels");
}

export async function createLabelApi(
  data: CreateLeadLabelInput,
): Promise<LeadLabel> {
  return api.post<LeadLabel>("/crm/labels", data);
}

export async function updateLabelApi(
  id: string,
  data: UpdateLeadLabelInput,
): Promise<LeadLabel> {
  return api.patch<LeadLabel>(`/crm/labels/${id}`, data);
}

export async function deleteLabelApi(id: string): Promise<void> {
  return api.delete<void>(`/crm/labels/${id}`);
}

export async function assignLabelApi(
  leadId: string,
  labelId: string,
): Promise<void> {
  return api.post<void>(`/crm/leads/${leadId}/labels`, { labelId });
}

export async function unassignLabelApi(
  leadId: string,
  labelId: string,
): Promise<void> {
  return api.delete<void>(`/crm/leads/${leadId}/labels/${labelId}`);
}

// ── Super-admin API ────────────────────────────────────────────────────────────

export type Plan = { id: string; key: string; name: string };

/** Wizard progress, embedded in every client payload. Null for no row yet. */
export type ClientOnboardingSummary = {
  currentStep: number;
  completedSteps: number[];
  completedAt: string | null;
};

export type SuperAdminClient = {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  whatsappTransport: WhatsappTransport;
  plan: Plan | null;
  widgetOrigins: string[];
  clientType: ClientTypeValue | null;
  // Optional on the way in: Vercel and Render deploy independently, so the CRM
  // can briefly read a backend that does not send it yet.
  brand?: BrandKey;
  city: string | null;
  timezone: string | null;
  onboarding: ClientOnboardingSummary | null;
  accountManagerId: string | null;
  accountManager: { id: string; name: string; email: string } | null;
  // Server-derived: this client's WhatsApp number is linked (Baileys session or WABA credentials).
  // The transport switch refuses while it is true — disconnect first.
  whatsappConnected: boolean;
  // The hosts its «Sitio web» answers on. Optional for the same deploy-skew reason as `brand`.
  siteHostnames?: string[];
};

/** The client fields step 1 of the wizard and the Clients form both write. */
export type ClientProfileInput = {
  name: string;
  planId: string | null;
  widgetOrigins: string[];
  clientType: ClientTypeValue | null;
  brand: BrandKey;
  city: string | null;
  timezone: string | null;
};

// The subset of a client returned by GET /crm/clients/mine, used to populate the
// account manager's client switcher.
export type ManagedClient = {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  // Present so the portfolio can show wizard progress and offer "continue" on a
  // school the account manager opened but has not finished configuring.
  onboarding: ClientOnboardingSummary | null;
};

export async function listMyClientsApi(): Promise<ManagedClient[]> {
  return api.get<{ clients: ManagedClient[] }>("/crm/clients/mine").then((r) => r.clients);
}

export async function updateClientTransportApi(
  id: string,
  transport: WhatsappTransport,
): Promise<SuperAdminClient> {
  return api.patch<SuperAdminClient>(`/super-admin/clients/${id}/transport`, { transport });
}

// Platform-side disconnect. The CRM's own /crm/whatsapp/logout is client-scoped, so it cannot
// reach another tenant — and the transport switch above needs a way to clear its 409.
export async function disconnectClientWhatsappApi(id: string): Promise<SuperAdminClient> {
  return api.post<SuperAdminClient>(`/super-admin/clients/${id}/whatsapp/disconnect`, {});
}

export async function listPlansApi(): Promise<Plan[]> {
  return api.get<{ plans: Plan[] }>("/super-admin/plans").then((r) => r.plans);
}

// ── Plans management (super-admin) ──────────────────────────────────────────────

/** A feature the Planes editor may associate (server-side allowlist). */
export type PlanFeatureOption = { id: string; key: string; name: string };

/** A plan with admin metadata — powers the Planes manager table + editor. */
export type ManagePlan = {
  id: string;
  key: string;
  name: string;
  position: number;
  isActive: boolean;
  featureIds: string[];
  clientCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CreatePlanBody = {
  key: string;
  name: string;
  position?: number;
  featureIds: string[];
};

export type UpdatePlanBody = {
  name: string;
  position?: number;
  featureIds: string[];
};

export async function listManagePlansApi(): Promise<ManagePlan[]> {
  return api.get<{ plans: ManagePlan[] }>("/super-admin/plans/manage").then((r) => r.plans);
}

export async function listFeaturesApi(): Promise<PlanFeatureOption[]> {
  return api.get<{ features: PlanFeatureOption[] }>("/super-admin/features").then((r) => r.features);
}

export async function createPlanApi(input: CreatePlanBody): Promise<ManagePlan> {
  return api.post<ManagePlan>("/super-admin/plans", input);
}

export async function updatePlanApi(id: string, input: UpdatePlanBody): Promise<ManagePlan> {
  return api.patch<ManagePlan>(`/super-admin/plans/${id}`, input);
}

export async function setPlanActiveApi(id: string, isActive: boolean): Promise<ManagePlan> {
  return api.patch<ManagePlan>(`/super-admin/plans/${id}/active`, { isActive });
}

// Feature keys granted by the active client's plan (drives CRM module gating).
export async function listClientFeaturesApi(): Promise<string[]> {
  return api.get<{ features: string[] }>("/crm/features").then((r) => r.features);
}

// The active client's institutional logo (already plan-gated server-side; null
// means "render the brand's own mark") plus which brand the CRM chrome renders.
//
// Returns the envelope rather than unwrapping to the logo, as it used to: the
// brand is a second answer from the same request, and two calls would let the
// sidebar's mark and its wordmark come from different reads.
export type ClientBranding = { logoUrl: string | null; brand: BrandKey };

export async function getClientBrandingApi(): Promise<ClientBranding> {
  const r = await api.get<{ logoUrl: string | null; brand?: BrandKey }>("/crm/branding");
  // `brand` is optional on the wire on purpose: Vercel and Render deploy
  // independently, so a CRM can briefly run against a backend that does not send
  // it yet. Degrading to the default brand is correct; crashing is not.
  return { logoUrl: r.logoUrl, brand: r.brand ?? DEFAULT_BRAND.key };
}

export async function uploadClientLogoApi(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm<{ logoUrl: string }>("/crm/settings/logo", form).then((r) => r.logoUrl);
}

export async function deleteClientLogoApi(): Promise<void> {
  await api.delete<void>("/crm/settings/logo");
}

export async function createClientApi(
  input: ClientProfileInput & { slug: string },
): Promise<SuperAdminClient> {
  return api.post<SuperAdminClient>("/super-admin/clients", input);
}

/** The hosts a client's «Sitio web» answers on — replaced as a set, normalized by the server. */
export async function setSiteHostnamesApi(id: string, hostnames: string[]): Promise<{ hostnames: string[] }> {
  return api.put<{ hostnames: string[] }>(`/super-admin/clients/${id}/site/hostnames`, { hostnames });
}

export async function updateClientApi(
  id: string,
  input: ClientProfileInput,
): Promise<SuperAdminClient> {
  return api.patch<SuperAdminClient>(`/super-admin/clients/${id}`, input);
}

export async function setClientActiveApi(
  id: string,
  isActive: boolean,
): Promise<SuperAdminClient> {
  return api.patch<SuperAdminClient>(`/super-admin/clients/${id}/active`, { isActive });
}

export async function setClientAccountManagerApi(
  id: string,
  accountManagerId: string | null,
): Promise<SuperAdminClient> {
  return api.patch<SuperAdminClient>(`/super-admin/clients/${id}/account-manager`, {
    accountManagerId,
  });
}

export type ConversationScript = {
  id: string;
  name: string;
  useCase: string;
  agentType: AgentType;
  version: number;
  status: "draft" | "active" | "archived";
  parsed: unknown;
  notes: string | null;
  createdAt: string;
};

export type AgentDeployment = {
  id: string;
  clientId: string;
  agentName: string;
  agentTone: string | null;
  agentRole: string | null;
  // null = inherit the system default (ANTHROPIC_MODEL + the model's own
  // defaultMaxTokens from the shared catalog).
  model: string | null;
  maxTokens: number | null;
  effort: string | null;
  engine: string | null;
  status: string;
  version: number;
  knowledgeBaseId: string;
  scriptId: string;
  createdAt: string;
  knowledgeBase: { id: string; version: number };
  script: { id: string; name: string; version: number };
};

export async function listClientsApi(): Promise<SuperAdminClient[]> {
  return api.get<{ clients: SuperAdminClient[] }>("/super-admin/clients").then((r) => r.clients);
}

export async function getClientApi(id: string): Promise<SuperAdminClient> {
  return api.get<SuperAdminClient>(`/super-admin/clients/${id}`);
}

export async function listScriptsApi(params?: {
  agentType?: string;
  status?: string;
}): Promise<ConversationScript[]> {
  const qs = params
    ? "?" + new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString()
    : "";
  return api
    .get<{ scripts: ConversationScript[] }>(`/super-admin/scripts${qs}`)
    .then((r) => r.scripts);
}

export async function getScriptApi(id: string): Promise<ConversationScript> {
  return api.get<ConversationScript>(`/super-admin/scripts/${id}`);
}

export async function activateScriptApi(id: string): Promise<ConversationScript> {
  return api.post<ConversationScript>(`/super-admin/scripts/${id}/activate`, {});
}

export async function archiveScriptApi(id: string): Promise<ConversationScript> {
  return api.post<ConversationScript>(`/super-admin/scripts/${id}/archive`, {});
}

export type ScriptDeploymentRef = {
  deploymentId: string;
  clientId: string;
  clientName: string;
  deploymentVersion: number;
  scriptVersion: number;
};

export async function listScriptDeploymentsApi(id: string): Promise<ScriptDeploymentRef[]> {
  return api
    .get<{ deployments: ScriptDeploymentRef[] }>(`/super-admin/scripts/${id}/deployments`)
    .then((r) => r.deployments);
}

export type ResourceVersion = { id: string; version: number; status: string; createdAt: string };

export async function listKnowledgeBasesApi(clientId: string): Promise<ResourceVersion[]> {
  return api
    .get<{ knowledgeBases: ResourceVersion[] }>(`/super-admin/clients/${clientId}/knowledge-bases`)
    .then((r) => r.knowledgeBases);
}

export async function listDeploymentsApi(clientId: string): Promise<AgentDeployment[]> {
  return api
    .get<{ deployments: AgentDeployment[] }>(`/super-admin/clients/${clientId}/deployments`)
    .then((r) => r.deployments);
}

export async function createDeploymentApi(
  clientId: string,
  // Mirrors `createDeploymentSchema`. `null` means "no override, use the default";
  // an OMITTED key means "inherit from the active deployment". The editor shows
  // every field below, so it always sends one or the other explicitly — never
  // undefined, which would silently carry the previous version's value forward.
  data: {
    agentName: string;
    agentTone: string | null;
    agentRole: string | null;
    model: SelectableModelId | null;
    maxTokens: number | null;
    effort: ModelEffort | null;
    engine: DeploymentEngine | null;
    knowledgeBaseId: string;
    scriptId: string;
  },
): Promise<AgentDeployment> {
  return api.post<AgentDeployment>(`/super-admin/clients/${clientId}/deployments`, data);
}

export type UsageQueryParams = {
  clientId: string;
  from?: string;
  to?: string;
  channel?: string;
};

export async function fetchUsageSummary(params: UsageQueryParams): Promise<LlmUsageSummaryResponse> {
  const qs = new URLSearchParams({ clientId: params.clientId });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.channel) qs.set("channel", params.channel);
  return api.get<LlmUsageSummaryResponse>(`/super-admin/usage?${qs.toString()}`);
}

// ── Billing (Stripe mirror) ─────────────────────────────────────────────────────
// Read-only except for the customer link. Stripe owns the money; these endpoints
// only read what the webhook mirrored.

export async function fetchBillingOverview(): Promise<BillingOverviewResponse> {
  return api.get<BillingOverviewResponse>("/super-admin/billing");
}

export async function fetchClientBilling(clientId: string): Promise<ClientBillingDetailResponse> {
  return api.get<ClientBillingDetailResponse>(`/super-admin/clients/${clientId}/billing`);
}

/** Links the `cus_...` pasted at close, or unlinks it with null. */
export async function setStripeCustomerApi(
  clientId: string,
  stripeCustomerId: string | null,
): Promise<{ adoptedEvents: number }> {
  return api.patch<{ adoptedEvents: number }>(
    `/super-admin/clients/${clientId}/stripe-customer`,
    { stripeCustomerId },
  );
}

/** What a school sees about its own subscription. Admin-only, server-enforced. */
export async function fetchMyBilling(): Promise<MyBillingResponse> {
  return api.get<MyBillingResponse>("/crm/billing");
}

// ── Lead programs of interest ─────────────────────────────────────────────
// No list endpoint: the interests ride in the lead-detail payload, so these
// mutations only have to invalidate `["lead", id]`.
export const addLeadInterestApi = (leadId: string, productId: string): Promise<void> =>
  api.post(`/crm/leads/${leadId}/interests`, { productId });

export const promoteLeadInterestApi = (leadId: string, interestId: string): Promise<void> =>
  api.patch(`/crm/leads/${leadId}/interests/${interestId}/primary`, {});

export const removeLeadInterestApi = (leadId: string, interestId: string): Promise<void> =>
  api.delete(`/crm/leads/${leadId}/interests/${interestId}`);

// ── People the lead is enquiring for ──────────────────────────────────────
// Same shape and same reasoning as the interests above: no list endpoint, the
// candidates ride in the lead-detail payload.
export const createLeadCandidateApi = (
  leadId: string,
  input: CreateLeadCandidateInput,
): Promise<void> => api.post(`/crm/leads/${leadId}/candidates`, input);

export const updateLeadCandidateApi = (
  leadId: string,
  candidateId: string,
  input: UpdateLeadCandidateInput,
): Promise<void> => api.patch(`/crm/leads/${leadId}/candidates/${candidateId}`, input);

export const removeLeadCandidateApi = (leadId: string, candidateId: string): Promise<void> =>
  api.delete(`/crm/leads/${leadId}/candidates/${candidateId}`);

/** Stripe Products offered in the link-generation picker — the client's brand only. */
export async function fetchBillingProducts(clientId: string): Promise<BillingProductOption[]> {
  return api
    .get<{ products: BillingProductOption[] }>(`/super-admin/clients/${clientId}/billing/products`)
    .then((r) => r.products);
}

export type CreatePaymentLinkBody = {
  productId: string;
  monthlyCents: number;
  setupCents: number;
  interval: "month" | "year";
  taxBehavior: "exclusive" | "inclusive";
};

export async function createPaymentLinkApi(
  clientId: string,
  body: CreatePaymentLinkBody,
): Promise<{ url: string; id: string }> {
  return api.post<{ url: string; id: string }>(
    `/super-admin/clients/${clientId}/payment-link`,
    body,
  );
}

export async function deletePaymentLinkApi(clientId: string): Promise<void> {
  await api.delete(`/super-admin/clients/${clientId}/payment-link`);
}

export async function cancelSubscriptionApi(
  clientId: string,
): Promise<{ cancelAt: string | null }> {
  return api.post<{ cancelAt: string | null }>(
    `/super-admin/clients/${clientId}/subscription/cancel`,
    {},
  );
}

export async function resumeSubscriptionApi(clientId: string): Promise<void> {
  await api.post(`/super-admin/clients/${clientId}/subscription/resume`, {});
}
